type InfiniteScrollerOptions = {
  axis: "x" | "y"
}

type ElementWithRect = {
  index: number;
  element: HTMLElement;
  rect: DOMRect;
}

class InfiniteScroller {

  private elements: HTMLElement[] = [];
  private elementOffsets: number[] = [];
  private scrollPosition = 0;
  private gap: number;

  constructor(private parent: HTMLElement, private options: InfiniteScrollerOptions) {
    parent.childNodes.forEach(child => {
      const el = child as HTMLElement;
      this.elements.push(el);
      this.elementOffsets.push(0);
    });
    this.gap = this.computeGap();
  }

  scroll(dist: number) {
    const parentRect = this.parent.getBoundingClientRect();
    const parentStart = this.getStart(parentRect);
    
    this.scrollPosition += dist;
    for (let index = 0; index < this.elements.length; index++) {
      this.moveElement(index, dist);
    }

    const sortedElements = this.getSortedElements();
    let maxEnd = this.getEnd(sortedElements[sortedElements.length - 1].rect);

    for (const se of sortedElements) {
      if (this.getEnd(se.rect) < parentStart) {
        const start = this.getStart(se.rect);
        const end = this.getEnd(se.rect);
        this.moveElement(se.index, maxEnd - start + this.gap);
        maxEnd += end - start;
      }
    }
  }

  private getSortedElements(): ElementWithRect[] {
    const result = this.elements.map((element, index) => {
      const rect = element.getBoundingClientRect();
      return { index, element, rect };
    });
    return result.sort((a, b) => this.getStart(a.rect) - this.getStart(b.rect));
  }

  scrollTo(position: number) {
    const dist = position - this.scrollPosition;
    this.scroll(dist);
  }

  reset() {
    this.scrollPosition = 0;
    this.gap = this.computeGap();
    this.elements.forEach((_, index) => {
      this.moveElement(index, -this.elementOffsets[index]);
    });
  }

  computeLoopDistance() {
    const parentRect = this.parent.getBoundingClientRect();
    const parentStart = this.getStart(parentRect);
    const max = this.computeMaxEnd();
    return max - parentStart + this.gap;
  }

  private get isHorizontal() {
    return this.options.axis === "x";
  }

  private computeMaxEnd() {
    let max = 0;
    for (const element of this.elements) {
      const rect = element.getBoundingClientRect();
      const end = this.getEnd(rect);
      if (end > max) max = end;
    }
    return max;
  }

  private computeGap() {
    if (!this.elements.length) return 0;
    const firstRect = this.elements[0].getBoundingClientRect();
    const secondRect = this.elements[1].getBoundingClientRect();
    return this.getStart(secondRect) - this.getEnd(firstRect);
  }

  private getStart(rect: DOMRect) {
    if (this.isHorizontal) {
      return rect.left;
    } else {
      return rect.top;
    }
  }

  private getEnd(rect: DOMRect) {
    if (this.isHorizontal) {
      return rect.right;
    } else {
      return rect.bottom;
    }
  }

  private moveElement(index: number, dist: number) {
    this.elementOffsets[index] += dist;
    const offset = this.elementOffsets[index];
    if (this.isHorizontal) {
      this.elements[index].style.transform = `translateX(${offset}px)`;
    } else {
      this.elements[index].style.transform = `translateY(${offset}px)`;
    }
  }
}

type CarouselOptions = {
  speed: number;
}

export class HorizontalCarousel {
  private scroller: InfiniteScroller;

  constructor(parent: HTMLElement, private options: CarouselOptions) {
    this.scroller = new InfiniteScroller(parent, { axis: "x" });
    this.start();
  }

  private start() {
    this.animate(dt => {
      if (dt > 100) {
        this.scroller.reset();
      } else {
        this.scroller.scroll(-dt * this.options.speed)
      }
    });
  }

  private animate(callback: (dt: number) => void) {
    let last = -1;
    function cb(timestamp: number) {
      if (last > 0) callback(timestamp - last);
      last = timestamp;
      requestAnimationFrame(cb);
    }
    requestAnimationFrame(cb);
  }
}
